import { LinkBox, LoadingDots } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useRecoilState, useRecoilValue } from 'recoil';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { useHippoText } from '../../../../hooks/useHippoText';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';
import { useState } from 'react';
import { identificationAtom } from '../../../../recoil/atoms/identificationAtom';
import {
    getCoverableFromAllOnsaCoverables,
    getCoverableIndex,
    getPropertyToUpdate
} from '../../../../utils/coverablesUtil';
import { isLoggedOnSelector } from '../../../../recoil/selectors/isLoggedOnSelector';
import { accountHolderAtom } from '../../../../recoil/atoms/accountHolderAtom';
import RemoveModal from '../../../../shared_components/RemoveModal/RemoveModal';
import { isAgeTooOldForFlavour, isCprTooOldForFlavour } from '../../../../utils/validationUtil';
import { calculateAge } from '../../../../utils/dateUtil';
import { allCoverablesAtom } from '../../../../recoil/atoms/allCoverablesAtom';

const WhoIsTheInsuranceForStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName, fixedId, lobCode } = currentProduct;
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const getHippoText = useHippoText();
    const { adultAccidents = [], youngAccidents = [] } = coverables;
    const coverableIndex =
        flavourName === 'Ulykkesforsikring'
            ? getCoverableIndex(adultAccidents, fixedId)
            : getCoverableIndex(youngAccidents, fixedId);
    const coverable =
        flavourName === 'Ulykkesforsikring'
            ? adultAccidents[coverableIndex]
            : youngAccidents[coverableIndex];
    const basketCoverables = useRecoilValue(allCoverablesAtom);
    const { cpr } = useRecoilValue(identificationAtom);
    const currentCoverable = getCoverableFromAllOnsaCoverables(coverables, fixedId, lobCode);
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const accountHolderData = useRecoilValue(accountHolderAtom);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [hasDoubleInsurance, setHasDoubleInsurance] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);

    const verifyDoubleInsurance = (): boolean => {
        const onsaCoverables = (
            flavourName === 'Ulykkesforsikring' ? adultAccidents : youngAccidents
        ).filter((cov) => cov.fixedId !== fixedId);
        const otherCoverables =
            flavourName === 'Ulykkesforsikring'
                ? basketCoverables.adultAccidents ?? []
                : basketCoverables.youngAccidents ?? [];
        const hasOwnInsurance = onsaCoverables.filter((cov) => !cov.insuranceForOtherPerson).length;
        const hasOwnInsuranceBasketCov = otherCoverables.filter(
            (cov) => !cov.isForOtherPerson
        ).length;
        return hasOwnInsurance + hasOwnInsuranceBasketCov >= 1;
    };

    const saveAccCoverable = (updatedCoverable: CoverableInformationToUpdateDTO): void => {
        setIsLoading(true);
        void saveCoverableInformation(updatedCoverable).then(() => {
            setIsLoading(false);
            goNext();
        });
    };

    const handleClick = (flavourName: string, isForOther: boolean): void => {
        if (isLoading || isRequestPending) return;
        if (coverableIndex === -1 || coverable === undefined) {
            return;
        }

        if (!isForOther) {
            let isTooOldForAccident = false;
            if (isLoggedOn) {
                const age = calculateAge(accountHolderData.dateOfBirth ?? {}, 40);
                isTooOldForAccident = isAgeTooOldForFlavour(age, flavourName);
            } else {
                isTooOldForAccident = isCprTooOldForFlavour(cpr, flavourName);
            }

            if (isTooOldForAccident) {
                setShowRemoveModal(true);
                return;
            }

            if (verifyDoubleInsurance()) {
                setHasDoubleInsurance(true);
                return;
            }
        }

        const accidentCoverables =
            flavourName === 'Ulykkesforsikring' ? adultAccidents : youngAccidents;
        const updatedCoverables = [...accidentCoverables];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            insuranceForOtherPerson: isForOther,
            name: isForOther ? undefined : accountHolderData.firstName
        };

        setCoverables((prev) => ({
            ...prev,
            [getPropertyToUpdate(flavourName)]: updatedCoverables
        }));

        const accidentType =
            flavourName === 'Ulykkesforsikring' ? 'adultAccident' : 'youngAccident';
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [accidentType]: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                insuranceForOtherPerson: isForOther
            },
            productToUpdate: flavourName
        };

        track(`${accidentType}CoverableInformationInsuranceForOtherPerson`, [
            {
                insuranceForOtherPerson: updatedCoverable[accidentType]?.insuranceForOtherPerson
            }
        ]);
        saveAccCoverable(updatedCoverable);
    };

    return (
        <WizardPagetemplate hidePrevious hideNext>
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-OTHER-HEADER')}
            >
                <div className="u-display-flex u-flex-column u-flex-items-start u-gap-5">
                    <LinkBox
                        className={
                            isLoading ? 'u-color-supplement-grey-medium flos-box' : 'flos-box'
                        }
                        title={getHippoText('DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-OTHER-1')}
                        onClick={() => {
                            handleClick(flavourName, false);
                        }}
                        arrowArea={
                            isLoading && !coverable.insuranceForOtherPerson ? (
                                <div className="u-spacing-stack-bottom-5 u-spacing-inset-right-6">
                                    <LoadingDots centered={false} />
                                </div>
                            ) : undefined
                        }
                    ></LinkBox>
                    <LinkBox
                        className={
                            isLoading ? 'u-color-supplement-grey-medium flos-box' : 'flos-box'
                        }
                        title={
                            flavourName === 'Ulykkesforsikring'
                                ? getHippoText('DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-OTHER-2')
                                : getHippoText(
                                      'DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-OTHER-YOUTH-2'
                                  )
                        }
                        onClick={() => {
                            handleClick(flavourName, true);
                        }}
                        arrowArea={
                            isLoading && coverable.insuranceForOtherPerson ? (
                                <div className="u-spacing-stack-bottom-5 u-spacing-inset-right-6">
                                    <LoadingDots centered={false} />
                                </div>
                            ) : undefined
                        }
                    >
                        {flavourName === 'Ulykkesforsikring'
                            ? getHippoText('DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-OTHER-2')
                            : getHippoText(
                                  'DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-OTHER-YOUTH-2'
                              )}
                    </LinkBox>
                </div>
            </CoverableDetailsTemplate>
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    title={
                        flavourName === 'Ulykkesforsikring'
                            ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-80-YEARS-TOO-OLD-HEADER')
                            : getHippoText('DSS-REMOVE-CONTINUE-MODAL-28-YEARS-TOO-OLD-HEADER')
                    }
                    description={
                        flavourName === 'Ulykkesforsikring'
                            ? getHippoText('DSS-REMOVE-CONTINUE-MODAL-80-YEARS-TOO-OLD-SUBHEADER')
                            : getHippoText('DSS-REMOVE-CONTINUE-MODAL-28-YEARS-TOO-OLD-SUBHEADER')
                    }
                    coverable={currentCoverable}
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                />
            )}
            {hasDoubleInsurance && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    isRemoveAndContinueModal
                    showModal={hasDoubleInsurance}
                    closeModal={() => {
                        setHasDoubleInsurance(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default WhoIsTheInsuranceForStep;
